// The bulk bar's one ZIP (spec bulk-zip): the worker makes it (spec assets/zip-download), and
// the bar waits for it, reading the job every second until it settles. A Download that fails
// while the ZIP may be the server's leaves its attempt for the next Download of the same files,
// which sends its key again and gets that ZIP, never a second one (spec bulk-zip decision 3).
import { getZip, startZip } from "../../api/generated/endpoints/assets/assets";
import { attemptKey, lostAttempt } from "../../platform/idempotency";
import { isSettled } from "../../platform/jobs/status";

/** Live's file name (`useAssetsDownload`). */
export const ZIP_NAME = "downloads.zip";
/** How often the bar reads the ZIP's job while the worker makes it. */
export const ZIP_POLL_MS = 1000;

/** The worker could not make the ZIP; its reason, in the API's words, when it gave one. */
export class ZipFailedError extends Error {
  override readonly name = "ZipFailedError";
}

/** A Download's ZIP: its key, what the key was sent for (the team, the brand and the files), and
 *  whether the start answered with the ZIP's job, which `zipped` marks. */
export interface ZipAttempt {
  readonly key: string;
  readonly body: string;
  started: boolean;
}

/** The kept attempt when it asked for these files, else a new one with a new key. */
export function zipAttempt(asked: unknown, kept: ZipAttempt | undefined): ZipAttempt {
  const key = attemptKey(asked, kept);
  return key === kept?.key ? kept : { key, body: JSON.stringify(asked), started: false };
}

/** Starts the attempt's ZIP of these files and waits until it is made: its URL, how many it
 *  holds, and how many the worker found gone and left out. A key sent again gets the job it
 *  started, as it stands, and counts no call (spec assets/zip-download). */
export async function zipped(
  teamId: string,
  brandId: string,
  assetIds: readonly string[],
  attempt: ZipAttempt,
  wait = ZIP_POLL_MS,
): Promise<{ url: string; files: number; skipped: number }> {
  const headers = { "Idempotency-Key": attempt.key };
  let zip = await startZip(teamId, brandId, { asset_ids: [...assetIds] }, headers);
  attempt.started = true;
  while (!isSettled(zip.status)) {
    await new Promise((resolve) => setTimeout(resolve, wait));
    zip = await getZip(teamId, brandId, zip.job_id);
  }
  if (zip.status !== "succeeded" || zip.url === null) throw new ZipFailedError(zip.error ?? "");
  return { url: zip.url, files: zip.files, skipped: zip.skipped };
}

/**
 * What a failed Download leaves for the next one of the same files. A start that answered made
 * the ZIP, so its attempt stays whatever failed after it, a read of the job or the save. A start
 * with no answer may have made it, so its attempt stays only while no answer came (proposal 3.2,
 * `lostAttempt`). A refused start, or a ZIP the worker could not make, leaves nothing: a new key.
 */
export function zipLeft(attempt: ZipAttempt, asked: unknown, error: unknown): ZipAttempt | undefined {
  if (error instanceof ZipFailedError) return undefined;
  if (attempt.started) return attempt;
  const lost = lostAttempt(attempt.key, asked, error);
  return lost === undefined ? undefined : { ...lost, started: false };
}
